import {
  emptyUiConfigOverlay,
  migrateUiConfigOverlay,
  type UiConfigOverlay,
} from "@repo/ui-config";

/**
 * Client store for the per-user UI-config overlay document.
 *
 * A module-level external store: imperative getters for non-React code
 * (formatting seam, table accessors) and a subscribe function for
 * `useSyncExternalStore`.
 *
 * localStorage is the BOOT CACHE, not the source of truth: the
 * server-persisted document (flag `addon_platform_ui_config`) wins once
 * the sync controller reconciles; this cache just avoids a flash of
 * default config at boot. Documents are treated as immutable values —
 * every write replaces the reference.
 */

const OVERLAY_STORAGE_KEY = "glider.ui-config.overlay";

/**
 * Which authenticated principal (USER id, never a wallet) the cached
 * overlay belongs to. Stamped by the server-sync controller on adoption
 * and successful pushes; checked before promoting a local cache to a
 * user's empty server document, and enforced at render time via
 * `quarantineUiConfigOverlayForPrincipal`.
 */
const OVERLAY_OWNER_STORAGE_KEY = "glider.ui-config.owner";

/**
 * Serialization of the overlay as of the last successful server sync.
 * Lets the boot reconcile distinguish a DIRTY cache (unsynced local edits
 * that must rebase onto the server document) from a cache that is merely
 * BEHIND a remote written by another device (which must adopt) — without
 * it, both look identical after a reload.
 */
const OVERLAY_LAST_SYNCED_STORAGE_KEY = "glider.ui-config.lastSynced";

type OverlayListener = () => void;

const listeners = new Set<OverlayListener>();

function readBootOverlay(): UiConfigOverlay {
  if (typeof window === "undefined") {
    return emptyUiConfigOverlay();
  }

  try {
    const raw = window.localStorage.getItem(OVERLAY_STORAGE_KEY);
    if (raw !== null) {
      const migrated = migrateUiConfigOverlay(JSON.parse(raw));
      if (migrated.ok) {
        return migrated.document;
      }
    }
  } catch {
    // Corrupt or unreadable cache: fall through to defaults.
  }

  return emptyUiConfigOverlay();
}

let overlayDocument: UiConfigOverlay = readBootOverlay();

function persistOverlay(overlay: UiConfigOverlay): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(OVERLAY_STORAGE_KEY, JSON.stringify(overlay));
  } catch {
    // Persistence is best-effort; in-memory state is authoritative for the
    // session.
  }
}

export function getUiConfigOverlay(): UiConfigOverlay {
  return overlayDocument;
}

export function setUiConfigOverlay(next: UiConfigOverlay): void {
  if (next === overlayDocument) {
    return;
  }

  overlayDocument = next;
  persistOverlay(next);
  for (const listener of listeners) {
    listener();
  }
}

export function updateUiConfigOverlay(
  update: (current: UiConfigOverlay) => UiConfigOverlay,
): void {
  setUiConfigOverlay(update(overlayDocument));
}

export function subscribeToUiConfigOverlay(
  listener: OverlayListener,
): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Once the authenticated principal is known, a cache stamped for a
 * DIFFERENT principal must not keep rendering (shared browser: user A's
 * hidden regions / lens state would show for user B). Discards the cached
 * overlay in that case; the previous user's state lives in their server
 * document. Unstamped caches (signed-out usage on this browser) are kept.
 */
export function quarantineUiConfigOverlayForPrincipal(
  principalKey: string,
): void {
  const owner = getUiConfigOverlayOwner();
  if (owner === null || owner === principalKey) {
    return;
  }
  setUiConfigOverlayOwner(principalKey);
  clearUiConfigLastSyncedSerialized();
  setUiConfigOverlay(emptyUiConfigOverlay());
}

export function getUiConfigOverlayOwner(): string | null {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    return window.localStorage.getItem(OVERLAY_OWNER_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setUiConfigOverlayOwner(ownerKey: string): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(OVERLAY_OWNER_STORAGE_KEY, ownerKey);
  } catch {
    // Best-effort; the safe failure mode is "no promotion".
  }
}

export function clearUiConfigLastSyncedSerialized(): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.removeItem(OVERLAY_LAST_SYNCED_STORAGE_KEY);
  } catch {
    // Ignore.
  }
}

export function getUiConfigLastSyncedSerialized(): string | null {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    return window.localStorage.getItem(OVERLAY_LAST_SYNCED_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setUiConfigLastSyncedSerialized(serialized: string): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(OVERLAY_LAST_SYNCED_STORAGE_KEY, serialized);
  } catch {
    // Best-effort; the safe failure mode is "treated as dirty".
  }
}

/** Test-only: clear persisted state and re-run the boot read. */
export function resetUiConfigOverlayForTest(): void {
  if (typeof window !== "undefined") {
    try {
      window.localStorage.removeItem(OVERLAY_STORAGE_KEY);
      window.localStorage.removeItem(OVERLAY_OWNER_STORAGE_KEY);
      window.localStorage.removeItem(OVERLAY_LAST_SYNCED_STORAGE_KEY);
    } catch {
      // Ignore.
    }
  }
  rebootUiConfigOverlayForTest();
}

/** Test-only: re-run the boot read against current storage (fresh-boot simulation). */
export function rebootUiConfigOverlayForTest(): void {
  overlayDocument = readBootOverlay();
  for (const listener of listeners) {
    listener();
  }
}
